import noop from '@lib/utils/noop';
import useTenantName from '@lib/hooks/useTenantName';
import { inboxLocators } from '@locators/inbox';
import { Button as LegacyButton, Headline } from '@servicetitan/design-system';
import {
  Button,
  Card,
  Chip,
  Flex,
  TextField,
  Tooltip,
} from '@servicetitan/anvil2';
import IconClose from '@servicetitan/anvil2/assets/icons/material/round/close.svg';
import { ChangeEvent, KeyboardEvent } from 'react';
import styles from './CustomerSearchBox.module.css';
import { useCustomerSearchContext } from '@components/CustomerSearch/context';

export type SearchParams = {
  name?: string;
  street?: string;
  unit?: string;
  zip?: string;
  phone?: string;
};

type Props = {
  tenantId?: string;
  disabled?: boolean;
  loading?: boolean;
  onSearch?: (params: SearchParams) => void;
  onChanged?: (params: SearchParams) => void;
  onCleared?: () => void;
};

const CustomerSearchBox = ({
  tenantId,
  disabled = false,
  loading = false,
  onSearch = noop,
  onChanged = noop,
  onCleared = noop,
}: Props) => {
  const {
    searchParams: params,
    setSearchParams: setParams,
    onAddCustomer,
  } = useCustomerSearchContext();

  const tenantName = useTenantName(tenantId ?? 'All Tenants');

  const handleSearch = () => {
    onSearch(params);
  };

  const handleClear = () => {
    onChanged({});
    setParams({
      name: '',
      phone: '',
      street: '',
      unit: '',
      zip: '',
    });
    onCleared();
  };

  const updateSearchParam = (key: keyof SearchParams, value: string) => {
    const newParams = { ...params, [key]: value };
    setParams(newParams);
    onChanged(newParams);
  };

  const emptyParams =
    !params.name &&
    !params.phone &&
    !params.street &&
    !params.unit &&
    !params.zip;

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !emptyParams) {
      handleSearch();
    }
  };

  return (
    <Card padding='large' className={styles.searchCard}>
      <Flex direction='column' gap='4'>
        <Flex alignItems='center' justifyContent='space-between'>
          <Flex gap='2' alignItems='center'>
            <Headline
              className='m-0'
              id={inboxLocators.customerSearch.searchTitle}
              size='small'
            >
              Search for Customer
            </Headline>
            <Chip label={tenantName} />
          </Flex>
          {onAddCustomer && (
            <LegacyButton
              id={inboxLocators.customerSearch.newCustomerBtn}
              fill='subtle'
              size='small'
              primary
              iconName='add_circle'
              disabled={disabled}
              onClick={onAddCustomer}
            >
              New Customer
            </LegacyButton>
          )}
        </Flex>

        <div className={styles.fieldsContainer}>
          <div className={styles.fieldFlex}>
            <TextField
              label='Name'
              id={inboxLocators.customerSearch.searchForm.name}
              value={params.name ?? ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateSearchParam('name', e.target.value)
              }
              onKeyDown={handleKeyDown}
              disabled={disabled}
            />
          </div>
          <div className={styles.fieldFlex}>
            <TextField
              label='Street'
              id={inboxLocators.customerSearch.searchForm.street}
              value={params.street ?? ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateSearchParam('street', e.target.value)
              }
              onKeyDown={handleKeyDown}
              disabled={disabled}
            />
          </div>
          <div className={styles.fieldUnit}>
            <TextField
              label='Unit'
              value={params.unit ?? ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateSearchParam('unit', e.target.value)
              }
              onKeyDown={handleKeyDown}
              disabled={disabled}
            />
          </div>
          <div className={styles.fieldZip}>
            <TextField
              label='ZIP Code'
              id={inboxLocators.customerSearch.searchForm.zip}
              value={params.zip ?? ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateSearchParam('zip', e.target.value)
              }
              onKeyDown={handleKeyDown}
              disabled={disabled}
            />
          </div>
          <div className={styles.fieldPhone}>
            <TextField
              label='Phone'
              id={inboxLocators.customerSearch.searchForm.phone}
              placeholder='(xxx) xxx-xxxx'
              value={params.phone ?? ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateSearchParam('phone', e.target.value)
              }
              onKeyDown={handleKeyDown}
              disabled={disabled}
            />
          </div>
          <div className={styles.fieldActions}>
            <Button
              appearance='primary'
              onClick={handleSearch}
              id={inboxLocators.customerSearch.searchBtn}
              disabled={disabled || loading || emptyParams}
              loading={loading}
            >
              Search
            </Button>
            {!emptyParams && (
              <Tooltip placement='bottom'>
                <Tooltip.Trigger>
                  <Button
                    aria-label='Clear Search'
                    appearance='ghost'
                    icon={IconClose}
                    disabled={disabled || loading || emptyParams}
                    onClick={handleClear}
                  />
                </Tooltip.Trigger>
                <Tooltip.Content>Clear Search</Tooltip.Content>
              </Tooltip>
            )}
          </div>
        </div>
      </Flex>
    </Card>
  );
};

export default CustomerSearchBox;
